import type { PaymentMethodBadgeColor } from '@cpce/database/enums';
import {
  type PaymentCounterparty,
  type PaymentListItem,
  type PaymentMethod,
  remiseTypeKey,
} from '@cpce/shared';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge, BadgeColor } from '@/components/Badge';
import { EntityLink } from '@/components/StyledLink';
import type { BadgeOption } from '@/components/table/table.types';
import { formatDate } from '@/utils/date.utils';
import { formatDecimalAsPrice } from '@/utils/string.utils';

// The badge color is frozen in database when the payment method is declared; this only maps it
// onto the component's palette. An account without a color falls back to gray.
const BADGE_COLOR_BY_PAYMENT_METHOD: Record<PaymentMethodBadgeColor, BadgeColor> = {
  blue: BadgeColor.BLUE,
  green: BadgeColor.GREEN,
  orange: BadgeColor.ORANGE,
  purple: BadgeColor.PURPLE,
  red: BadgeColor.RED,
  gray: BadgeColor.GRAY,
};

export function resolveBadgeColor(color: PaymentMethodBadgeColor | null): BadgeColor {
  return color ? BADGE_COLOR_BY_PAYMENT_METHOD[color] : BadgeColor.GRAY;
}

export function CounterpartyCell({ counterparty }: { counterparty: PaymentCounterparty }) {
  if (!counterparty) return <span className="text-gray-500">-</span>;
  return (
    <EntityLink entity={counterparty.kind} id={counterparty.id}>
      {counterparty.name}
    </EntityLink>
  );
}

// A remise row carries no instrument number — a deposit holds several — while a payment row does.
type PaymentMethodRow = { paymentMethod: PaymentMethod; instrumentNumber?: string | null };

export function buildPaymentMethodOptions(rows: PaymentMethodRow[]): BadgeOption<number>[] {
  return [
    ...new Map(
      rows.map((row) => [row.paymentMethod.financialAccountId, row.paymentMethod])
    ).values(),
  ].map((paymentMethod) => ({
    value: paymentMethod.financialAccountId,
    label: paymentMethod.name,
    color: resolveBadgeColor(paymentMethod.badgeColor),
  }));
}

// What one remise may hold: an account taking cheques and cards yields two.
export function buildRemiseTypeOptions(rows: PaymentMethodRow[]): BadgeOption<string>[] {
  return [
    ...new Map(rows.map((row) => [remiseTypeKey(row.paymentMethod), row.paymentMethod])).values(),
  ].map((paymentMethod) => ({
    value: remiseTypeKey(paymentMethod),
    label: paymentMethod.name,
    color: resolveBadgeColor(paymentMethod.badgeColor),
  }));
}

// Filtered on the payment method, not on the displayed text: the cell also holds the instrument
// number, which differs on every row and would make the filter useless.
export function paymentMethodColumn<T extends PaymentMethodRow>(
  paymentMethodOptions: BadgeOption<number>[]
): ColumnDef<T, never> {
  return {
    id: 'paymentMethod',
    accessorFn: (row: T) => row.paymentMethod.financialAccountId,
    header: 'Type',
    cell: ({ row }) => {
      const { paymentMethod, instrumentNumber } = row.original;
      return (
        <div className="flex flex-col gap-0.5">
          <Badge color={resolveBadgeColor(paymentMethod.badgeColor)}>{paymentMethod.name}</Badge>
          {instrumentNumber && <span className="text-xs text-gray-500">{instrumentNumber}</span>}
        </div>
      );
    },
    meta: { filterType: 'badge-enum', options: paymentMethodOptions },
  } as ColumnDef<T, never>;
}

export function counterpartyColumn<T extends PaymentListItem>(header: string): ColumnDef<T, never> {
  return {
    id: 'counterparty',
    accessorFn: (row: T) => row.counterparty?.name ?? '-',
    header,
    cell: ({ row }) => <CounterpartyCell counterparty={row.original.counterparty} />,
    meta: { filterType: 'text' },
  } as ColumnDef<T, never>;
}

export function emissionDateColumn<T extends PaymentListItem>(): ColumnDef<T, never> {
  return {
    id: 'emissionDate',
    accessorFn: (row: T) => row.emissionDate,
    header: "Date d'émission",
    cell: ({ row }) => formatDate(row.original.emissionDate),
    meta: { filterType: 'date' },
  } as ColumnDef<T, never>;
}

export function amountColumn<T extends PaymentListItem>(): ColumnDef<T, never> {
  return {
    id: 'amount',
    accessorFn: (row: T) => row.amount.toNumber(),
    header: 'Montant',
    cell: ({ row }) => formatDecimalAsPrice(row.original.amount),
    meta: { filterType: 'numeric' },
  } as ColumnDef<T, never>;
}
